import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";

const TYPES = ["partner","sponsor","sanctuary","funder","university","media","government"];
const STAGES = [{v:"prospect",l:"Prospect"},{v:"exploring",l:"Exploring"},{v:"active",l:"Active"},{v:"dormant",l:"Dormant"}];
const STAGE_COLOR = { prospect: "bg-forest/10 text-forest", exploring: "bg-terracotta/20 text-terracotta", active: "bg-terracotta text-bone", dormant: "bg-ink-soft/20 text-ink-soft" };

export default function Partnerships() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/partnerships"); setItems(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/partnerships/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Development · Partnerships</div>
          <h1 className="font-serif text-4xl mt-1">Partnership Opportunities</h1>
        </div>
        <NewPartnership onCreated={load} />
      </div>

      <div className="bg-admin-surface border border-line rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone/50 text-ink-soft">
            <tr>{["Organization","Contact","Type","Stage",""].map(h => <th key={h} className="text-left px-4 py-3 font-medium uppercase tracking-widest text-xs">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.map(p => (
              <tr key={p.id} data-testid={`partnership-row-${p.id}`} className="hover:bg-bone/40">
                <td className="px-4 py-3 font-medium text-ink">{p.org_name}</td>
                <td className="px-4 py-3 text-ink-soft"><div>{p.contact_name}</div><div className="text-xs">{p.contact_email}</div></td>
                <td className="px-4 py-3"><Badge variant="secondary" className="bg-forest/10 text-forest">{p.type}</Badge></td>
                <td className="px-4 py-3"><Badge className={STAGE_COLOR[p.stage] || "bg-forest/10 text-forest"}>{p.stage}</Badge></td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(p.id)} className="text-ink-soft hover:text-terracotta"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan="5" className="text-center text-ink-soft py-10">No partnerships tracked yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewPartnership({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ org_name: "", contact_name: "", contact_email: "", type: "partner", stage: "prospect", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/partnerships", f); toast.success("Added"); setOpen(false); onCreated(); setF({ org_name: "", contact_name: "", contact_email: "", type: "partner", stage: "prospect", notes: "" }); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="pill pill-primary text-sm" data-testid="new-partnership-btn"><Plus className="h-4 w-4" /> New Partnership</button></DialogTrigger>
      <DialogContent className="bg-bone">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Partnership</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="overline">Organization</Label><Input required data-testid="np-org" value={f.org_name} onChange={e=>setF({...f, org_name: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Contact name</Label><Input data-testid="np-contact-name" value={f.contact_name} onChange={e=>setF({...f, contact_name: e.target.value})} /></div>
            <div><Label className="overline">Contact email</Label><Input type="email" data-testid="np-contact-email" value={f.contact_email} onChange={e=>setF({...f, contact_email: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Type</Label>
              <Select value={f.type} onValueChange={v => setF({...f, type: v})}>
                <SelectTrigger data-testid="np-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Stage</Label>
              <Select value={f.stage} onValueChange={v => setF({...f, stage: v})}>
                <SelectTrigger data-testid="np-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(v => <SelectItem key={v.v} value={v.v}>{v.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="overline">Notes</Label><Textarea rows={3} data-testid="np-notes" value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} /></div>
          <button type="submit" data-testid="np-submit" className="pill pill-primary">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
